import { useMemo, useState } from "react";
import { Button } from "../ui/Button";
import { IconCheck, IconLink } from "@tabler/icons-react";
import { ButtonProps, Space } from "@mantine/core";
import { useActiveUserPartner } from "@/store/selectors";
import { Analytics } from "@/services/Analytics";
import { buildShareableLink, getPlaylistLink } from "@/lib/shareableLink";

export function CopyPlaylistLink({
  playlistId,
  size = "xs",
  propsLink = "",
  disabled = false,
  fullWidth = false,
  text = "Copy link",
  onClick,
  context,
  buttonProps,
}: {
  playlistId?: string;
  size?: "xs" | "sm" | "md";
  iconSize?: number;
  propsLink?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  text?: string;
  context?: string;
  onClick?: (value: string) => void;
  buttonProps?: ButtonProps;
}) {
  const [copied, setCopied] = useState(false);
  const userPartner = useActiveUserPartner();
  const partnerSlug = userPartner?.slug;

  const copyToClipboard = () => {
    const baseLink = getPlaylistLink(playlistId!, {
      propsLink: propsLink || undefined,
      origin: window.location.origin,
    });
    const link = buildShareableLink(baseLink, { partnerSlug });
    navigator.clipboard.writeText(link);
    setCopied(true);

    onClick?.(link);

    Analytics.click({
      action: "copy_link",
      url: link,
      label: "Copy link",
      context,
    });

    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  const iconSize = useMemo(() => {
    if (size === "xs") return 12;
    if (size === "sm") return 16;
    if (size === "md") return 20;
    return 12;
  }, [size]);

  const buttonHeight = useMemo(() => {
    if (size === "xs") return 24;
    if (size === "sm") return 30;
    if (size === "md") return 36;
    return 24;
  }, [size]);

  const fontSize = useMemo(() => {
    if (size === "xs") return 10;
    if (size === "sm") return 12;
    if (size === "md") return 14;
    return 10;
  }, [size]);

  return (
    <Button
      variant="light"
      radius="xl"
      size={size}
      h={buttonHeight}
      fz={fontSize}
      py={0}
      // px={size}
      fullWidth={fullWidth}
      onClick={copyToClipboard}
      disabled={disabled}
      leftSection={
        size === "xs" ? null : copied ? (
          <IconCheck size={iconSize} />
        ) : (
          <IconLink size={iconSize} />
        )
      }
      {...buttonProps}
    >
      {size === "xs" ? (
        copied ? (
          <IconCheck size={iconSize} />
        ) : (
          <IconLink size={iconSize} />
        )
      ) : null}
      {size === "xs" ? <Space w={4} /> : null}
      {copied ? "Copied!" : text}
    </Button>
  );
}

export const CopyLink = CopyPlaylistLink;
